import { useContext, useState } from "react"

import { useLazyQuery, useMutation } from "@apollo/client";
import { Box, CircularProgress, Grid, IconButton, Menu, MenuItem, Typography } from "@mui/material"
import { v4 as uuid } from 'uuid';

import { GET_RECIPE_E3_FROM_ID } from "../../../app/graphql/queries";
import { setEditSubstep, setOldRecipeData } from "../../../app/recipeCreation/createRecipe.slice"
import { Substep } from "../../../app/recipeCreation/createRecipe.types";
import { newRecipeDataSelector, setRecipe } from "../../../app/recipePage/recipePage.slice";
import { useAppDispatch, useAppSelector } from "../../../app/store/hooks"
import useDeleteSubstep from "../../../hooks/useDeleteSubstep";
import useLogRecipeEdit from "../../../hooks/useLogRecipeEdit";
import { ErrorManagementContext } from "../../../utils/errorManagement/ErrorManagementProvider";
import { EventLogsEnum } from "../../../utils/events";
import { VerticalIcon } from "../../../utils/iconRefs/recipePageIcons"
import { recipeE3TransformerQL2FS } from "../../../utils/RecipeBackEnd/hasuraFSMigrationTransformers";
import { COLEN, LocationEnum, MULTIPLE_PHRASE, SINGULAR_PHRASE, SubStepEditEnum, SubStepTypeEnum } from "../utils/constants"
import LocationIcon from "../utils/getIcons"

function capitalizeFirstLetter(inputString: string): string {
    if (inputString?.length === 0) {
        // Return an empty string if the input is empty
        return '';
    }

    // Capitalize the first letter and concatenate the rest of the string
    const capitalizedString = inputString?.charAt(0).toUpperCase() + inputString?.slice(1);

    return capitalizedString;
}

const options = [
    {
        text: 'Edit',
        type: SubStepEditEnum.EDIT_SUBSTEP,
    },
    {
        text: 'Remove step',
        type: SubStepEditEnum.REMOVE_SUBSTEP,
    },
    {
        text: 'Add a step below',
        type: SubStepEditEnum.ADD_SUBSTEP,
    }
];

const LocationUI = ({ location, name, type, userCreatedRecipe, substepId, stepId, stepIndex, substep, substepIndex, subStepUId, onEdit }: { location: string, name: string, type?: string, userCreatedRecipe: boolean, substepId: string, stepId: string | null, stepIndex: number, substep: Substep, substepIndex: number, subStepUId: string, onEdit?: () => void }) => {
    const dispatch = useAppDispatch();
    const { onDelete, loading: deleting } = useDeleteSubstep();
    const [fetchRecipe, { loading: recipeLoading }] = useLazyQuery(GET_RECIPE_E3_FROM_ID, {
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'network-only',
    });
    const { cookClubLog } = useContext(ErrorManagementContext);
    const editStepView = useAppSelector((state) => state.recipeCreation.editStepView);
    const newRecipeData = useAppSelector(newRecipeDataSelector);
    const oldRecipe = useAppSelector((state) => state.recipeCreation.oldRecipeData);
    const recipeVariationId = oldRecipe?.Recipe_Variations.find((recipeVar) => recipeVar.default)?.id;
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const { logRecipeEdit } = useLogRecipeEdit();

    const handleEditMenuClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const onCompleted = async () => {
        const fetchedData = await fetchRecipe({
            variables: {
                id: oldRecipe?.id,
            }
        });
        const newRecipeData = fetchedData.data.Recipes_E3_by_pk;
        
        dispatch(setRecipe({ recipe: recipeE3TransformerQL2FS(newRecipeData) }));
        dispatch(setOldRecipeData(newRecipeData))
        
    }

    const handleEditOption = (operationType: SubStepEditEnum) => {

        if (operationType === SubStepEditEnum.REMOVE_SUBSTEP) {
            const recipeSteps = newRecipeData?.steps.find(step => step.id === stepId);
            const substepsLength: number = recipeSteps ? recipeSteps.subSteps.length : 0;
            const stepContainsOnlyOneSubstep = substepsLength === 1;

            logRecipeEdit(EventLogsEnum.APP_REMOVE_SUB_STEP, {
                eventData: { substepId }
            })

            if (oldRecipe && recipeVariationId && stepId) {
                onDelete({
                    oldRecipe,
                    recipeVariationId,
                    stepId,
                    substep,
                    subStepUId,
                    deleteStepAt: stepIndex,
                    deleteSubStepAt: substepIndex,
                    isDeleteStep: stepContainsOnlyOneSubstep,
                    onCompleted,
                    onError: (err: any) => {
                        cookClubLog(`Something went wrong. Try Again!`, "", {}, { ui: { type: "info" }, browserConsole: true });
                    },
                });
            }
        }
        else {
            if (operationType === SubStepEditEnum.ADD_SUBSTEP) {

                const addNewSubstep = {
                    id: uuid(),
                    subStepUId: uuid(),
                    name: '',
                    type: SubStepTypeEnum.OUTSIDE_INSTRUCTION,
                    media: { url: "", loop: true, muted: true, title: "", duration: 0, startTime: 0 },
                    parameters: {
                        location: LocationEnum.NONE,
                        outsideDescription: '',
                    },
                    created_at: null,
                    updated_at: null,
                    Recipe_Presets: [],
                    SubSteps_Ingredients_E3s: [],
                }

                dispatch(setEditSubstep({ substep: addNewSubstep, substepOperation: operationType, substepId, subStepUId, substepIndex, stepId }));
                logRecipeEdit(EventLogsEnum.APP_ADD_SUB_STEP, {
                    eventData: { substepId }
                })
            }
            if (operationType === SubStepEditEnum.EDIT_SUBSTEP) {
                dispatch(setEditSubstep({ substep, substepOperation: operationType, substepId, subStepUId, substepIndex, stepId }));
                logRecipeEdit(EventLogsEnum.APP_RECIPE_SUB_STEP_EDIT, {
                    eventData: { substepId }
                })
            }
            setAnchorEl(null);
        }
    }

    return (
        <Box sx={{
            backgroundColor: ((type === SubStepTypeEnum.OUTSIDE_INSTRUCTION || type === SubStepTypeEnum.INGREDIENTS) && location.toLowerCase() !== LocationEnum.JAR) ? '#F1ECFF' : '#FFF3D0',
            width: '100%',
            display: 'flex',
            padding: editStepView ? '0 0 0 10px' : '10px',
            boxSizing: 'border-box',
            justifyContent: 'space-between',
            alignItems: 'center'
        }}>
            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    {location?.toLowerCase() !== LocationEnum.NONE && <>
                        <LocationIcon location={location} />
                        <Typography variant='captionparalell'>
                            In {location?.toLowerCase() !== LocationEnum.JAR ? SINGULAR_PHRASE : MULTIPLE_PHRASE}
                            {capitalizeFirstLetter(location)}
                            {!!name && COLEN}
                        </Typography>
                    </>}
                    <Typography variant='caption3Light'>{name}</Typography>
                </Box>
                {userCreatedRecipe &&
                    < Grid >
                        <IconButton
                            aria-label="more"
                            id="long-button"
                            onClick={handleEditMenuClick}
                            style={{ padding: '0px' }}
                        >
                            <VerticalIcon />
                        </IconButton>
                        <Menu
                            anchorEl={anchorEl}
                            id="long-menu"
                            onClose={handleClose}
                            open={open}
                        >
                            {options.map((option) => (
                                <MenuItem key={option.type} onClick={() => handleEditOption(option.type)}>
                                    <Grid container direction='row' justifyContent='space-between'>
                                        {option.text}
                                        {option.type === SubStepEditEnum.REMOVE_SUBSTEP && (deleting || recipeLoading) && <CircularProgress disableShrink style={{ width: 20, height: 20, color: "#000", padding: 5 }} />}
                                    </Grid>
                                </MenuItem>
                            ))}
                        </Menu>
                    </Grid>
                }
            </Box>
        </Box >
    )
}

export default LocationUI